import {
  Box,
  Drawer,
  IconButton,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import DataTableLayout from "../../Common/UI/DataTableLayout";
import DataTableFooter from "../../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../../utils/dataTableShell";
import { MoreVert, Settings } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { DateTime } from "luxon";
import { errorToastMessage } from "../../../utils/toast";
import AddGroupDrawer from "./Drawers/AddGroupDrawer";
import DeleteGroupModal from "./Modals/DeleteGroupModal";
// import SendInvitationDrawer from "./Drawers/SendInvitationDrawer";
import { useAppSelector } from "../../../Redux/hooks";
import { permissions } from "../../../utils/roles";
import ViewCohortDrawer from "./Drawers/ViewCohortDrawer";

type Props = {
  refreshPage: () => void;
  toggleLoader: boolean;
};

const GroupsList = ({ refreshPage, toggleLoader }: Props) => {
  const [showEditGroup, setShowEditGroup] = useState<boolean>(false);
  const [groupId, setGroupId] = useState("");
  // const [showInvitation, setShowInvitation] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const sitePermissions = useAppSelector(
    (state) => state.study.sitePermissions
  );
  const dataEditable = useAppSelector((state) => state.study.dataEditable);
  const studyPermissions = useAppSelector(
    (state) => state.study.studyPermissions
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [page, setPage] = useState<number>(0);
  const open = Boolean(anchorEl);
  const { id: studyId } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [groupCount, setGroupCount] = useState<number>(0);
  const [data, setData] = useState<any[]>([]);
  const [canEdit] = useState(
    studyPermissions.includes(permissions.manageCohort) && dataEditable
  );
  const navigate = useNavigate();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const closeEditGroup = () => {
    setShowEditGroup(false);
    setSelectedRow(null);
  };
  const closeDeleteModal = () => {
    setConfirmDelete(false);
    setSelectedRow(null);
  };
  // const closeInvitationDrawer = () => {
  //   setShowInvitation(false);
  //   setSelectedRow(null);
  // };

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/participant-groups?page=${
            page + 1
          }&size=${pageSize}`
        );
        const { data } = res.data;
        const newData = data?.data?.map((item: any) => {
          return {
            id: item?.id,
            groupName: item?.name || "-",
            participantCount: item?.participantsTotalCount,
            targetParticipantCount: item?.targetCount,
            color: item?.colorCode || null,
            code: item?.code || null,
            // site: item?.site?.id,
            // siteName: item?.site?.name || "-",
            createdOn: DateTime.fromISO(item?.createdAt).toFormat("MM/dd/yyyy"),
            // canEdit:
            //   sitePermissions?.[item?.site?.id]?.permissions?.includes(
            //     permissions?.addParticipant
            //   ) && dataEditable,
            // sendSurvey:
            //   sitePermissions?.[item?.site?.id]?.permissions?.includes(
            //     permissions?.sendSurveys
            //   ) && dataEditable,
          };
        });

        setData(newData || []);
        setGroupCount(data?.meta?.paginationOptions?.totalItems || 0);
      } catch (error) {
        errorToastMessage(error as Error);
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    if (studyId) {
      fetchData();
    }
  }, [studyId, toggleLoader, page, sitePermissions, dataEditable]);

  // const openCohortDrawer = (id: string) => {
  //   setGroupId(id);
  // };

  const closeCohortDrawer = () => {
    setGroupId("");
  };

  const navigateToParticipants = (row: any) => {
    const id = row?.id;
    const name = row?.groupName
      ? `?name=${encodeURIComponent(row.groupName)}`
      : "";
    navigate(
      `/studies/${studyId}/participant-groups/${id}/participants${name}`
    );
  };

  const navigateToCohortBuilder = (row: any) => {
    navigate(
      `/studies/${studyId}/participant-groups/${row?.id}/filter?name=${row?.groupName}`
    );
  };

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        footer={
          <DataTableFooter
            filteredCount={groupCount}
            page={page}
            pageSize={pageSize}
            onPageChange={handleChangePage}
            loading={loading}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ minWidth: 130 }}>
                User Group Name
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: 120 }}>Code</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 100 }}>Color</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 180 }}>
                Target Participant Count
              </StyledTableCell>
              {/* <StyledTableCell sx={{ minWidth: 180 }}>Site</StyledTableCell> */}
              <StyledTableCell sx={{ minWidth: 180 }}>
                Participant Count
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: 180 }}>
                Created On
              </StyledTableCell>
              <StyledTableCell></StyledTableCell>
            </TableRow>
          </TableHead>
          {groupCount > 0 && !loading && (
            <TableBody>
              {data?.map((row: any) => (
                <TableRow key={row?.id}>
                  <StyledTableCell
                    onClick={() => navigateToParticipants(row)}
                    sx={{ cursor: "pointer" }}
                  >
                    {row?.groupName}
                  </StyledTableCell>
                  <StyledTableCell>{row?.code || "-"}</StyledTableCell>
                  <StyledTableCell>
                    {row?.color ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "4px",
                            backgroundColor: row.color,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {row.color.toUpperCase()}
                        </Typography>
                      </Box>
                    ) : (
                      "-"
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {row?.targetParticipantCount ?? "-"}
                  </StyledTableCell>
                  {/* <StyledTableCell>{row?.siteName}</StyledTableCell> */}
                  <StyledTableCell>{row?.participantCount}</StyledTableCell>
                  <StyledTableCell>{row?.createdOn}</StyledTableCell>
                  {
                    // row?.canEdit || row?.sendSurvey
                    canEdit ? (
                      <StyledTableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                          }}
                        >
                          <IconButton
                            onClick={() => navigateToCohortBuilder(row)}
                          >
                            <Settings />
                          </IconButton>
                          <IconButton
                            onClick={(e) => handleMenuClick(e, row)}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    ) : (
                      <StyledTableCell></StyledTableCell>
                    )
                  }
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!loading && groupCount === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            minHeight: "50px",
            minWidth: "150px",
          },
        }}
      >
        {canEdit && (
          <MenuItem
            key="edit"
            onClick={() => {
              setShowEditGroup(true);
              handleMenuClose();
            }}
          >
            <ListItemText sx={{ fontWeight: 600, fontSize: "16px" }}>
              Edit
            </ListItemText>
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem
            key="criteria-filter"
            onClick={() => {
              navigateToCohortBuilder(selectedRow);
              handleMenuClose();
            }}
          >
            <ListItemText sx={{ fontWeight: 600, fontSize: "16px" }}>
              Criteria Filter
            </ListItemText>
          </MenuItem>
        )}
        {/* {canEdit && (
            <MenuItem
              key="send-invitation"
              onClick={() => {
                setShowInvitation(true);
                handleMenuClose();
              }}
            >
              <ListItemText sx={{ fontWeight: 600, fontSize: "16px" }}>
                Send Survey Package Invitation
              </ListItemText>
            </MenuItem>
          )} */}
        {canEdit && (
          <MenuItem
            key="delete"
            onClick={() => {
              setConfirmDelete(true);
              handleMenuClose();
            }}
          >
            <ListItemText
              sx={{ fontWeight: 600, fontSize: "16px", color: "#F05252" }}
            >
              Delete
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Drawer open={showEditGroup} onClose={closeEditGroup} anchor={"right"}>
        <AddGroupDrawer
          closeDrawer={closeEditGroup}
          data={selectedRow}
          refreshPage={refreshPage}
        />
      </Drawer>
      {groupId && (
        <Drawer
          open={groupId ? true : false}
          onClose={closeCohortDrawer}
          anchor="right"
        >
          <ViewCohortDrawer
            closeDrawer={closeCohortDrawer}
            groupId={groupId}
          />
        </Drawer>
      )}
      {/* <Drawer
          open={showInvitation}
          onClose={closeInvitationDrawer}
          anchor={"right"}
        >
          <SendInvitationDrawer
            closeDrawer={closeInvitationDrawer}
            data={selectedRow}
          />
        </Drawer> */}
      {confirmDelete && (
        <DeleteGroupModal
          openModal={confirmDelete}
          closeModal={closeDeleteModal}
          data={selectedRow}
          refreshPage={refreshPage}
        />
      )}
    </Box>
  );
};

export default GroupsList;
